// frontend/src/components/layout/SyncProgressPill.jsx
//
// The import pill at the bottom of the sidebar: every import of the store,
// told behind the app while the merchant works in it. A first import walks
// five steps — locations, catalog, inventory levels (the run itself), then the
// order history landing after the run reads `completed`, then the forecast
// run those orders start (the ONBOARDING pipeline). A refresh is one quiet
// line. It reads the same sync-status query the entry order seeded
// (`hooks/useInAppSyncStatus`: one key, one poll loop, at the interval
// `utils/syncGate.syncPollInterval` names).
//
// States, in this order:
//   - read failed:  the last read of the payload failed — loud, so a pill
//                  frozen on an old state never reads as the import's, with
//                  Try again: the poll may have stopped on the old state
//                  (`syncPollInterval` reads the last payload it has);
//   - run failed:   the latest run failed, with what it asks
//                  (`utils/syncGate.failureState`): reconnect the store,
//                  wait for Shopify, or Contact support. It stays until a
//                  new run starts: it is not a flash;
//   - run active:   a refresh says "Updating your store data"; a first import
//                  names its catalog phase (`utils/syncGate.phaseState`) with
//                  the step and the records written so far, and "preparing"
//                  for the order history once its phases are through;
//   - importing:    the orders landing (`utils/syncGate.ordersLanding`) —
//                  `progress.orders.synced + skipped` of `progress.orders.total`
//                  (the orders export's root object count), or "preparing"
//                  with no count while Shopify has not answered the export;
//   - calculating: the forecast run is pending or running
//                  (`utils/syncGate.forecastRunState`);
//   - forecast failed: a loud state with Contact support, shown whenever the
//                  payload says so. The queue may still re-run it, and the
//                  pill goes back to calculating
//                  (backend/shopify_app/services/sync_status.py);
//   - finished:    what the last end this session watched finished, until the
//                  merchant dismisses it or a state above takes the pill:
//                  the forecast in (a first import's last step: "Import
//                  finished"), the orders in (`ordersSynced`, with the landed
//                  payload's total — a small store lands between two polls, so
//                  the last landing read may have had none), or the run
//                  itself. A landing that ends without the orders in, or a run
//                  or calculation that ends failed, finishes nothing: the
//                  failed state says so;
//   - hidden:      otherwise.
// Every end this session saw (run active off, landing off, calculating off)
// makes every page read again once (`queries/dataChanged`, everything): the
// pages and the Home tiles hold data read before the catalog, the orders or
// the forecast were in, and nothing else tells them to read again. Each end is
// keyed on the sync run id, so a page opened after the end shows nothing
// finished.
//
// A phase count is every record the run visited (`utils/syncGate.visitedCount`):
// a restarted import skips the records it already wrote.

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ContactSupportButton from '../common/ContactSupportButton';
import { phaseNouns } from '../sync/phaseCopy';
import { useAuth } from '../../contexts/AuthContext';
import { useFormatters } from '../../hooks/useFormatters';
import { useInAppSyncQuery } from '../../hooks/useInAppSyncStatus';
import { DATA, useDataChanged } from '../../queries/dataChanged';
import {
  CATALOG_PHASES, FAILURE_STATE, FORECAST_RUN, catalogImported, failureState,
  forecastRunState, isRefreshSync, isSyncFinished, ordersLanding, phaseState, visitedCount,
} from '../../utils/syncGate';
import { ICON, ArrowsClockwise, CheckCircle, WarningCircle, X } from '../../icons';

// A first import's steps: the three catalog phases, the order history, the
// forecast.
const STEP_COUNT = 5;
const ORDERS_STEP = 4;
const FORECAST_STEP = 5;

const PHASE_LABEL_KEYS = {
  locations: 'syncScreen.pill.locations',
  products: 'syncScreen.pill.catalog',
  inventory: 'syncScreen.pill.inventory',
};

function orderCounts(syncStatus) {
  const orders = syncStatus?.sync?.progress?.orders;
  if (!orders) return { visited: 0, total: null };
  return { visited: orders.synced + orders.skipped, total: orders.total };
}

// Below 100 while landing: unparseable orders count in neither `synced` nor
// `skipped`, so `visited` can end short of `total`; only the finished pill
// fills the bar.
function landingPercent(visited, total) {
  if (!(total > 0)) return 0;
  return Math.min(99, Math.floor((visited / total) * 100));
}

/**
 * Calls `onEnd` on the render `active` flips off for the run it flipped on
 * for: the end of a window this session watched.
 *
 * Adjusting state during render is React's own alternative to a
 * setState-in-effect; the equality guards stop it looping.
 */
function useWatchedEnd(active, runId, onEnd) {
  const [seenRunId, setSeenRunId] = useState(null);
  if (active && seenRunId !== runId) {
    setSeenRunId(runId);
  }
  if (!active && seenRunId !== null && seenRunId === runId) {
    setSeenRunId(null);
    onEnd();
  }
}

/** The label and lines of a finished pill, read when it renders. */
function finishedCopy(t, formatNumber, { what, firstImport, total }) {
  if (what === 'orders') {
    return {
      label: t('syncScreen.landing.ready'),
      lines: [t('syncScreen.landing.readyCount', { total: formatNumber(total, 0) })],
    };
  }
  if (what === 'forecast') {
    return firstImport
      ? { label: t('syncScreen.pill.importFinished'), lines: [t('syncScreen.forecast.ready')] }
      : { label: t('syncScreen.forecast.ready') };
  }
  return {
    label: firstImport ? t('syncScreen.pill.importFinished') : t('syncScreen.pill.refreshFinished'),
  };
}

/**
 * One pill. `tone` picks the frame (progress, ready, warning, failed);
 * `bar` is a percentage, 'indeterminate', or null for none; `lines` are the
 * small lines under the bar, in order; `onDismiss`, when given, puts a close
 * button on the label row.
 */
const TONE_ICONS = {
  progress: ArrowsClockwise, ready: CheckCircle, warning: WarningCircle, failed: WarningCircle,
};

function Pill({ tone = 'progress', label, pct = null, bar = null, lines = [], onDismiss = null, children }) {
  const { t } = useTranslation();
  const loud = tone === 'warning' || tone === 'failed';
  const Icon = TONE_ICONS[tone];
  const className = tone === 'progress' ? 'sidebar-progress' : `sidebar-progress sidebar-progress--${tone}`;
  return (
    <div className={className} {...(loud ? { role: 'alert' } : { role: 'status', 'aria-live': 'polite' })}>
      <div className="sidebar-progress-row">
        <Icon size={ICON.md} className="sidebar-progress-icon" />
        <span className="sidebar-progress-label">{label}</span>
        {pct !== null && <span className="sidebar-progress-pct">{pct}%</span>}
        {onDismiss && (
          <button
            type="button"
            className="sidebar-progress-dismiss"
            aria-label={t('common.dismiss')}
            onClick={onDismiss}
          >
            <X size={ICON.sm} />
          </button>
        )}
      </div>
      {bar === 'indeterminate' && (
        <div className="sidebar-progress-bar sidebar-progress-bar--indeterminate">
          <div className="sidebar-progress-fill" />
        </div>
      )}
      {typeof bar === 'number' && (
        <div className="sidebar-progress-bar">
          <div className="sidebar-progress-fill" style={{ width: `${bar}%` }} />
        </div>
      )}
      {lines.map((line) => <div key={line} className="sidebar-progress-count">{line}</div>)}
      {children}
    </div>
  );
}

function RunFailedPill({ run }) {
  const { t } = useTranslation();
  const { selectedStore } = useAuth();
  const failure = failureState(run.failureReason);

  if (failure === FAILURE_STATE.RECONNECT) {
    return (
      <Pill tone="warning" label={t('syncScreen.heading.reconnect')}>
        <div className="sidebar-progress-body">{t('syncScreen.pill.reconnectBody')}</div>
        <a className="sidebar-progress-action" href={selectedStore.shopify_sign_in_url}>
          {t('syncScreen.reconnect')}
        </a>
      </Pill>
    );
  }
  if (failure === FAILURE_STATE.WAITING_FOR_SHOPIFY) {
    return (
      <Pill tone="warning" label={t('syncScreen.heading.waitingForShopify')}>
        <div className="sidebar-progress-body">{t('syncScreen.pill.waitingBody')}</div>
      </Pill>
    );
  }
  let label;
  if (isRefreshSync(run.syncType)) label = t('syncScreen.heading.refreshFailed');
  else if (catalogImported(run)) label = t('syncScreen.pill.orderHistoryFailed');
  else label = t('syncScreen.heading.importFailed');
  return (
    <Pill tone="failed" label={label}>
      <ContactSupportButton />
    </Pill>
  );
}

const SyncProgressPill = () => {
  const { t } = useTranslation();
  const { formatNumber } = useFormatters();
  const query = useInAppSyncQuery();
  const syncStatus = query.data ?? null;

  const run = syncStatus?.sync ?? null;
  const runId = run?.id ?? null;
  const runActive = run !== null && !isSyncFinished(run.status);
  const firstImport = run !== null && !isRefreshSync(run.syncType);
  const step = (n) => t('syncScreen.pill.step', { step: n, total: STEP_COUNT });
  const landing = ordersLanding(syncStatus);
  const forecastRun = forecastRunState(syncStatus);
  const calculating = forecastRun === FORECAST_RUN.CALCULATING;
  const { visited, total } = orderCounts(syncStatus);

  // `ends` counts the ends this session watched; `finished` is the pill the
  // last one left, null when it finished nothing or was dismissed.
  const changed = useDataChanged();
  const [ends, setEnds] = useState(0);
  const [finished, setFinished] = useState(null);
  const end = (pill) => {
    setEnds((n) => n + 1);
    setFinished(pill);
  };
  useWatchedEnd(runActive, runId, () => end(
    run.status === 'completed' ? { what: 'run', firstImport } : null));
  useWatchedEnd(landing, runId, () => end(
    syncStatus.ordersSynced ? { what: 'orders', total } : null));
  useWatchedEnd(calculating, runId, () => end(
    forecastRun === FORECAST_RUN.READY ? { what: 'forecast', firstImport } : null));
  useEffect(() => {
    if (ends > 0) changed([DATA.everything]);
  }, [ends, changed]);

  if (query.isError) {
    return (
      <Pill tone="failed" label={t('syncScreen.pill.readFailed')}>
        <button type="button" className="sidebar-progress-action" onClick={() => query.refetch()}>
          {t('entry.retry')}
        </button>
      </Pill>
    );
  }

  if (run?.status === 'failed') return <RunFailedPill run={run} />;

  if (runActive) {
    if (!firstImport) {
      return <Pill label={t('syncScreen.updatingStoreData')} bar="indeterminate" />;
    }
    const index = CATALOG_PHASES.findIndex((phase) => phaseState(phase, run) === 'active');
    if (index === -1) {
      // Its catalog phases are through: the run is submitting the orders export.
      return <Pill label={t('syncScreen.landing.preparing')} bar="indeterminate" lines={[step(ORDERS_STEP)]} />;
    }
    const phase = CATALOG_PHASES[index];
    return (
      <Pill
        label={t(PHASE_LABEL_KEYS[phase.id])}
        bar="indeterminate"
        lines={[`${step(index + 1)} · ${phaseNouns(t, formatNumber, phase, run.progress, visitedCount)}`]}
      />
    );
  }

  if (landing) {
    if (total === null) {
      return (
        <Pill
          label={t('syncScreen.landing.preparing')}
          bar="indeterminate"
          lines={firstImport ? [step(ORDERS_STEP)] : []}
        />
      );
    }
    const pct = landingPercent(visited, total);
    return (
      <Pill
        label={t('syncScreen.landing.importing')}
        pct={pct}
        bar={pct}
        lines={[t('syncScreen.landing.count', {
          visited: formatNumber(visited, 0), total: formatNumber(total, 0),
        })]}
      />
    );
  }

  if (calculating) {
    return (
      <Pill
        label={t('syncScreen.forecast.calculating')}
        bar="indeterminate"
        lines={firstImport ? [step(FORECAST_STEP)] : []}
      />
    );
  }

  if (forecastRun === FORECAST_RUN.FAILED) {
    return (
      <Pill tone="failed" label={t('syncScreen.forecast.failed')}>
        <ContactSupportButton />
      </Pill>
    );
  }

  if (finished) {
    return (
      <Pill
        tone="ready"
        bar={100}
        {...finishedCopy(t, formatNumber, finished)}
        onDismiss={() => setFinished(null)}
      />
    );
  }

  return null;
};

export default SyncProgressPill;
